import React, { useMemo } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Typography,
  Box,
  Paper,
  CircularProgress
} from '@mui/material';
import { formatPercentage, getValueColor } from 'utils/metricFormatters';

// Array of month names for column headers
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Extract year from date string (YYYY-MM-DD)
 * 
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {number} The year as a number
 */
const extractYear = (dateString) => {
  return parseInt(dateString.split('-')[0], 10);
};

/**
 * Extract month from date string (YYYY-MM-DD)
 * 
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {number} The month as a number (0-11)
 */
const extractMonth = (dateString) => {
  return parseInt(dateString.split('-')[1], 10) - 1; // JavaScript months are 0-indexed
};

/**
 * Calculates the percentage change between two values
 * 
 * @param {number} endValue - Ending value
 * @param {number} startValue - Starting value
 * @returns {number} Percentage change as a decimal (e.g., 0.05 for 5%)
 */
const calculatePercentChange = (endValue, startValue) => {
  if (!startValue || !endValue) return null;
  
  // Ensure we're working with precise numbers
  const start = parseFloat(startValue);
  const end = parseFloat(endValue);
  
  // Calculate percentage change with higher precision
  return (end - start) / start;
};

/**
 * Group performance data by year and month
 * 
 * @param {Array} performanceData - Array of performance data objects with date and value
 * @returns {Object} Data organized by year and month
 */
const organizeDataByYearMonth = (performanceData) => {
  if (!performanceData || !performanceData.length) return {};

  // Sort data by date (ascending)
  const sortedData = [...performanceData].sort((a, b) => 
    new Date(a.date) - new Date(b.date)
  );

  const dataByYearMonth = {};
  
  // Process data month by month to correctly identify first and last values
  const monthData = {};
  
  // Group all data points by year-month
  sortedData.forEach(item => {
    const date = new Date(item.date);
    const year = date.getFullYear();
    const month = date.getMonth();
    const yearMonth = `${year}-${month}`;
    
    if (!monthData[yearMonth]) {
      monthData[yearMonth] = [];
    }
    
    monthData[yearMonth].push({
      date: date,
      value: parseFloat(item.value)
    });
  });
  
  // For each month, get the first and last values
  Object.keys(monthData).forEach(yearMonth => {
    const [year, month] = yearMonth.split('-').map(Number);
    const monthPoints = monthData[yearMonth];
    
    // Sort points in this month by date
    monthPoints.sort((a, b) => a.date - b.date);
    
    // Get the last data point for this month
    const lastDayValue = monthPoints[monthPoints.length - 1].value;
    
    // Initialize year object if needed
    if (!dataByYearMonth[year]) {
      dataByYearMonth[year] = {};
    }
    
    // Store the last day's value for monthly returns calculation
    dataByYearMonth[year][month] = lastDayValue;
  });
  
  return dataByYearMonth;
};

/**
 * Calculate monthly returns from the organized data
 * 
 * @param {Object} dataByYearMonth - Data organized by year and month
 * @param {number} minCapital - Minimum capital for the strategy, used for initial month calculation
 * @returns {Object} Monthly returns by year and month
 */
const calculateMonthlyReturns = (dataByYearMonth, minCapital) => {
  const returns = {};
  const years = Object.keys(dataByYearMonth).sort((a, b) => parseInt(a) - parseInt(b));
  
  // Convert minCapital to a number if it's a string
  const initialCapital = minCapital !== null ? 
    (typeof minCapital === 'string' ? parseFloat(minCapital) : minCapital) : null;
  
  // Create a flat list of all months in chronological order
  const allMonths = [];
  years.forEach(year => {
    const yearNum = parseInt(year);
    Object.keys(dataByYearMonth[year]).forEach(month => {
      const monthNum = parseInt(month);
      allMonths.push({
        year: yearNum,
        month: monthNum,
        value: dataByYearMonth[year][month]
      });
    });
  });
  
  // Sort chronologically
  allMonths.sort((a, b) => {
    if (a.year !== b.year) return a.year - b.year;
    return a.month - b.month;
  });
  
  // Now iterate through all months in order for a proper sequential calculation
  for (let i = 0; i < allMonths.length; i++) {
    const current = allMonths[i];
    const { year, month, value } = current;
    const yearStr = year.toString();
    
    // Initialize year object if needed
    if (!returns[yearStr]) {
      returns[yearStr] = {};
    }
    
    let returnValue = null;
    
    if (i === 0 && initialCapital) {
      // First month ever, use initialCapital as reference
      returnValue = calculatePercentChange(value, initialCapital);
    } else if (i > 0) {
      // Compare to previous month
      const previous = allMonths[i-1];
      returnValue = calculatePercentChange(value, previous.value);
    }
    
    returns[yearStr][month] = returnValue;
  }
  
  // Calculate YTD returns for each year
  years.forEach(year => {
    const monthsInYear = Object.keys(returns[year]).sort((a, b) => parseInt(a) - parseInt(b));
    
    let ytdReturn = 1;
    let hasValidReturns = false;
    
    monthsInYear.forEach(month => {
      if (returns[year][month] !== null) {
        ytdReturn *= (1 + returns[year][month]);
        hasValidReturns = true;
      }
    });
    
    returns[year].ytd = hasValidReturns ? ytdReturn - 1 : null;
  });
  
  return returns;
};

// Format percentage values with appropriate precision
const formatReturn = (value) => {
  if (value === null || value === undefined) return 'N/A';
  
  // Format with appropriate precision based on the value magnitude
  if (Math.abs(value) < 0.0001) {
    // If truly zero (less than 0.01%), show as 0.0%
    return '0.0%';
  } else if (Math.abs(value) < 0.01) {
    // For small values (less than 1%), use 2 decimal places
    return (value * 100).toFixed(2) + '%';
  } else if (Math.abs(value) < 0.1) {
    // For medium values (less than 10%), use 1 decimal place
    return (value * 100).toFixed(1) + '%';
  } else {
    // For larger values, use 1 decimal place
    return (value * 100).toFixed(1) + '%';
  }
};

/**
 * PerformanceReturnsTable - Component to display monthly returns in a table format
 * 
 * @param {Object} props
 * @param {Array} props.performanceData - Array of performance data objects with date and value
 * @param {string} props.title - Title for the table
 * @param {boolean} props.loading - Whether data is loading
 * @param {number} props.minCapital - The minimum capital of the strategy
 * @returns {JSX.Element}
 */
const PerformanceReturnsTable = ({ 
  performanceData, 
  title = 'Monthly Returns',
  loading = false,
  minCapital = null
}) => {
  // Process performance data to get monthly returns
  const monthlyReturns = useMemo(() => {
    if (!performanceData || loading) return {};
    
    const dataByYearMonth = organizeDataByYearMonth(performanceData);
    
    // Get direct calculation results
    const calculatedReturns = calculateMonthlyReturns(dataByYearMonth, minCapital);
    
    // Force direct calculation for the first month using minCapital
    if (minCapital && performanceData && performanceData.length > 0) {
      // Find the first month in the performance data
      const sortedData = [...performanceData].sort((a, b) => 
        new Date(a.date) - new Date(b.date)
      );
      
      if (sortedData.length > 0) {
        const firstItem = sortedData[0];
        const firstDate = new Date(firstItem.date);
        const firstYear = firstDate.getFullYear().toString();
        const firstMonth = firstDate.getMonth();
        
        // Ensure we have the required objects initialized
        if (!calculatedReturns[firstYear]) {
          calculatedReturns[firstYear] = {};
        }
        
        // Get the end value for the first month
        const firstValue = parseFloat(firstItem.value);
        
        // Get the start value (minCapital)
        const parsedMinCapital = typeof minCapital === 'string' 
          ? parseFloat(minCapital) 
          : minCapital;
        
        // Calculate and directly set the first month's return
        if (parsedMinCapital && parsedMinCapital > 0) {
          const firstMonthReturn = (firstValue - parsedMinCapital) / parsedMinCapital;
          calculatedReturns[firstYear][firstMonth] = firstMonthReturn;
          
          // Also recalculate YTD for this year to include this fixed first month
          let ytdReturn = 1;
          let hasValidReturns = false;
          
          Object.keys(calculatedReturns[firstYear])
            .filter(m => m !== 'ytd')
            .forEach(month => {
              if (calculatedReturns[firstYear][month] !== null) {
                ytdReturn *= (1 + calculatedReturns[firstYear][month]);
                hasValidReturns = true;
              }
            });
          
          calculatedReturns[firstYear].ytd = hasValidReturns ? ytdReturn - 1 : null;
        }
      }
    }
    
    return calculatedReturns;
  }, [performanceData, loading, minCapital]);
  
  // Get years for table rows (descending order)
  const years = useMemo(() => {
    return Object.keys(monthlyReturns).sort((a, b) => b - a);
  }, [monthlyReturns]);
  
  if (loading) {
    return (
      <Box sx={{ my: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (!performanceData || performanceData.length === 0) {
    return (
      <Box sx={{ my: 3 }}>
        <Typography variant="h6">{title}</Typography>
        <Typography color="text.secondary">No performance data available</Typography>
      </Box>
    );
  }
  
  return (
    <Box sx={{ my: 3 }}>
      <Typography variant="h6">{title}</Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Includes system fee and broker commissions
      </Typography>
      
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              {MONTHS.map((month) => (
                <TableCell key={month} align="center">
                  {month}
                </TableCell>
              ))}
              <TableCell align="center">
                <Typography fontWeight="bold">YTD</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {years.map((year) => (
              <TableRow key={year} hover>
                <TableCell>
                  <Typography variant="subtitle1">{year}</Typography>
                </TableCell>
                
                {MONTHS.map((_, monthIndex) => {
                  const returnValue = monthlyReturns[year][monthIndex];
                  
                  // Determine color based on return value
                  const color = returnValue !== null 
                    ? getValueColor(returnValue) 
                    : 'text.secondary';
                  
                  return (
                    <TableCell 
                      key={`${year}-${monthIndex}`} 
                      align="center"
                      sx={{ 
                        color,
                        fontWeight: returnValue !== null ? 500 : 400
                      }}
                    >
                      {returnValue !== null ? formatReturn(returnValue) : 'N/A'}
                    </TableCell>
                  );
                })}
                
                {/* YTD column */}
                <TableCell 
                  align="center"
                  sx={{ 
                    color: monthlyReturns[year].ytd !== null 
                      ? getValueColor(monthlyReturns[year].ytd) 
                      : 'text.secondary',
                    fontWeight: 700
                  }}
                >
                  {monthlyReturns[year].ytd !== null 
                    ? formatReturn(monthlyReturns[year].ytd) 
                    : 'N/A'
                  }
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PerformanceReturnsTable; 